/**
 * Compilation-tab compatibility helpers.
 *
 * The platform's training pipeline always emits a 3D output tensor of
 * shape (batch_size, forecast_horizon, num_labels). Some metrics that
 * Keras ships - R2Score, F1Score, Precision, Recall, AUC,
 * accuracy, top_k_accuracy - were designed for 2D outputs. The backend
 * wraps them in a Flatten3DMetric adapter (see
 * core/src/app/models/metric_adapters.py) so they "just work", but
 * users should be told which ones are wrapped so they understand the
 * semantics (per-step metric, not per-sequence).
 *
 * Some loss functions are also fundamentally incompatible with the
 * platform's regression-shaped targets. We surface those as explicit
 * "incompatible" rather than wrapping (the backend validator will
 * reject them at create time).
 */

// Regression metrics: identity-passthrough, no wrapping needed.
export const REGRESSION_METRICS = [
  'mae',
  'mse',
  'rmse',
  'mape',
  'cosine_similarity',
];

// Classification-style metrics: wrapped server-side via
// Flatten3DMetric so they compute per-step against 3D outputs.
export const WRAPPED_CLASSIFICATION_METRICS = [
  'r2_score',
  'accuracy',
  'precision',
  'recall',
  'f1_score',
  'auc',
  'top_k_accuracy',
];

// Full metric catalog (same chip order as before; categorization
// drives the per-chip tooltip / hint).
export const ALL_METRICS = [
  ...REGRESSION_METRICS,
  ...WRAPPED_CLASSIFICATION_METRICS,
];

// Loss functions that work for any-rank regression outputs.
export const REGRESSION_LOSSES = [
  'huber',
  'mse',
  'mae',
  'logcosh',
  'cosine_similarity',
  'kl_divergence',
];

// Loss functions that need class-probability targets - rejected by
// the backend validator for forecasting blueprints.
export const CLASSIFICATION_LOSSES = [
  'binary_crossentropy',
  'categorical_crossentropy',
  'sparse_categorical_crossentropy',
  'focal_loss',
  'hinge',
  'squared_hinge',
];

export const ALL_LOSSES = [...REGRESSION_LOSSES, ...CLASSIFICATION_LOSSES];

/**
 * Classify a metric for UI hint rendering.
 * @param {string} name lowercase metric alias
 * @returns {'regression' | 'wrapped' | 'unknown'}
 */
export const metricCategory = (name) => {
  const n = (name || '').toLowerCase();
  if (REGRESSION_METRICS.includes(n)) return 'regression';
  if (WRAPPED_CLASSIFICATION_METRICS.includes(n)) return 'wrapped';
  return 'unknown';
};

/**
 * Classify a loss function for UI hint rendering.
 * @param {string} name lowercase loss alias
 * @returns {'regression' | 'classification' | 'unknown'}
 */
export const lossCategory = (name) => {
  const n = (name || '').toLowerCase();
  if (REGRESSION_LOSSES.includes(n)) return 'regression';
  if (CLASSIFICATION_LOSSES.includes(n)) return 'classification';
  return 'unknown';
};

/**
 * Returns a tooltip / helper string for a metric chip explaining its
 * compatibility with the platform's 3D forecasting output, or null if
 * no hint is needed (regression metrics are silent).
 */
export const metricHint = (name) => {
  const cat = metricCategory(name);
  if (cat === 'wrapped') {
    return (
      'Wrapped for 3D forecasting outputs - the platform flattens (B, H, L) ' +
      'into (B*H, L) before computing this metric, giving you a per-step value.'
    );
  }
  return null;
};

/**
 * Returns an array of warnings (strings) for the current
 * compilation_details. Empty array means the configuration is sound.
 *
 * Used to render an inline alert on the Compilation tab so users see
 * the same issues the backend validator would surface, BEFORE they
 * click Save.
 */
export const validateCompilationCompatibility = (compilationDetails) => {
  const warnings = [];
  const loss = (compilationDetails?.loss || '').toLowerCase();
  const metrics = compilationDetails?.metrics || [];

  if (loss && lossCategory(loss) === 'classification') {
    warnings.push(
      `Loss "${loss}" is a classification loss; this platform's ` +
      'forecasting models produce regression-shaped targets and the ' +
      'model will be rejected at save. Pick a regression loss ' +
      `(${REGRESSION_LOSSES.join(', ')}).`
    );
  }

  if (metrics.length === 0) {
    warnings.push(
      'At least one metric must be selected so training has something to ' +
      'monitor. Recommended: mae and mse.'
    );
  }

  return warnings;
};
